import React, { useState, useEffect } from 'react';
import { C, _T } from '../../shared/theme';
import { getConnectionStrength } from '../../shared/dbService';

// Connection tiers
const TIERS = [
  { min: 0,  max: 14,  icon: '🌱', labelNL: 'Nieuw verbonden', labelEN: 'Just connected',  color: C.silver  },
  { min: 15, max: 35,  icon: '👋', labelNL: 'Kennis',          labelEN: 'Acquaintance',    color: '#5B9ED0' },
  { min: 36, max: 65,  icon: '🤝', labelNL: 'Verbonden',       labelEN: 'Connected',       color: '#2A9E82' },
  { min: 66, max: 89,  icon: '💪', labelNL: 'Hechte band',     labelEN: 'Strong bond',     color: '#D4873A' },
  { min: 90, max: 100, icon: '⭐', labelNL: 'Diepe connectie', labelEN: 'Deep connection', color: '#C8A84B' },
];

function getTier(strength) {
  return TIERS.find(t => strength >= t.min && strength <= t.max) || TIERS[0];
}

/**
 * ConnectionStrength
 * Shows a compact connection indicator between currentUserId and targetUserId.
 * mode: 'badge' (icon + label) | 'dots' (5 dots) | 'bar' (mini progress bar)
 */
function ConnectionStrength({ currentUserId, targetUserId, lang, mode = 'dots', showLabel = false }) {
  const nl = lang === 'nl';
  const [strength, setStrength] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
      setLoading(false);
      return;
    }
    getConnectionStrength(currentUserId, targetUserId).then(data => {
      setStrength(data?.strength ?? 0);
      setLoading(false);
    });
  }, [currentUserId, targetUserId]);

  if (loading || strength === null) {
    return <div style={{ width: 60, height: 10, background: `${C.silver}20`, borderRadius: 4 }} />;
  }

  const tier = getTier(strength);
  const dots = 5;
  const filledDots = Math.round((strength / 100) * dots);

  if (mode === 'badge') {
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: `${tier.color}15`, border: `1px solid ${tier.color}33`,
        borderRadius: 20, padding: '3px 10px',
      }}>
        <span style={{ fontSize: 12 }}>{tier.icon}</span>
        <span style={{ fontSize: 10, color: tier.color, fontWeight: 600 }}>
          {nl ? tier.labelNL : tier.labelEN}
        </span>
      </div>
    );
  }

  if (mode === 'bar') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 48, height: 4, background: `${tier.color}20`, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${strength}%`, background: tier.color, borderRadius: 4, transition: 'width 0.6s ease' }} />
        </div>
        {showLabel && <span style={{ fontSize: 10, color: tier.color }}>{nl ? tier.labelNL : tier.labelEN}</span>}
      </div>
    );
  }

  // Default: 'dots'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ display: 'flex', gap: 3 }}>
        {Array.from({ length: dots }).map((_, i) => (
          <div key={i} style={{
            width: 7, height: 7, borderRadius: '50%',
            background: i < filledDots ? tier.color : `${tier.color}28`,
            transition: 'background 0.3s',
          }} />
        ))}
      </div>
      {showLabel && (
        <span style={{ fontSize: 10, color: tier.color, fontWeight: 600 }}>
          {nl ? tier.labelNL : tier.labelEN}
        </span>
      )}
    </div>
  );
}

export default ConnectionStrength;
